import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  HiPlus, HiChevronDown, HiChevronRight, HiCheck, HiX, HiArrowRight, HiChat, HiPlusCircle, HiPaperAirplane, HiLockClosed,
} from 'react-icons/hi';
import { Button, Select, Card, CardHeader, Badge, Pagination, Modal, Input } from '../components/ui';
import {
  listRequests, getMyRequests, getRequest, createRequest, processRequest, forwardRequest, addRequestComment,
} from '../services/requests.api';
import { listCustomers } from '../services/customers.api';
import { listPartners } from '../services/partners.api';
import { getActiveProducts } from '../services/products.api';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { formatDate, formatDateTime, parseJSON, getRuntimeLabel } from '../utils/helpers';
import { REQUEST_TYPE_OPTIONS, REQUEST_STATUS_OPTIONS, RUNTIME_OPTIONS } from '../utils/constants';
import { getErrorMessage } from '../services/api';
import type { PartnerRequest, RequestComment, RequestStatus, RequestStage, RequestType, Runtime } from '../types';

const typeLabel = (t: string) => REQUEST_TYPE_OPTIONS.find((o) => o.value === t)?.label || t;

const stageLabel: Record<RequestStage, string> = {
  PARTNER: 'beim Partner',
  ADMIN: 'beim Admin',
  OWNER: 'beim Owner',
  DONE: 'erledigt',
};

const stageClasses: Record<RequestStage, string> = {
  PARTNER: 'bg-amber-100 text-amber-700',
  ADMIN: 'bg-blue-100 text-blue-700',
  OWNER: 'bg-purple-100 text-purple-700',
  DONE: 'bg-gray-100 text-gray-600',
};

const commentActionLabel: Record<string, string> = {
  CREATED: 'Antrag erstellt',
  COMMENT: 'Kommentar',
  FORWARDED: 'An Admin weitergeleitet',
  APPROVED: 'Genehmigt',
  REJECTED: 'Abgelehnt',
  COMPLETED: 'Abgeschlossen',
};

function commentIcon(action: string) {
  switch (action) {
    case 'CREATED': return <HiPlusCircle className="w-4 h-4 text-green-500" />;
    case 'FORWARDED': return <HiArrowRight className="w-4 h-4 text-blue-500" />;
    case 'APPROVED': return <HiCheck className="w-4 h-4 text-green-500" />;
    case 'REJECTED': return <HiX className="w-4 h-4 text-red-500" />;
    case 'COMPLETED': return <HiCheck className="w-4 h-4 text-green-600" />;
    default: return <HiChat className="w-4 h-4 text-gray-500" />;
  }
}

export function Requests() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isPartner = user?.role === 'PARTNER';
  const isCustomer = user?.role === 'CUSTOMER';
  const canProcess = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'INVOICE_TEAM';
  const canCreate = isPartner || isCustomer;

  // Viewing the requests list clears the "new" badge in the sidebar.
  const markRequestsSeen = useNotificationStore((state) => state.markRequestsSeen);
  useEffect(() => {
    if (user) markRequestsSeen(user.id);
  }, [user, markRequestsSeen]);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<RequestStatus | ''>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentVisibleToCustomer, setCommentVisibleToCustomer] = useState(false);
  const [forwardText, setForwardText] = useState('');
  const [processModalRequest, setProcessModalRequest] = useState<PartnerRequest | null>(null);

  // Create form state
  const [customerId, setCustomerId] = useState('');
  const [requestType, setRequestType] = useState<RequestType>('EXTEND_LICENSE');
  const [productId, setProductId] = useState('');
  const [deviceCount, setDeviceCount] = useState(1);
  const [runtime, setRuntime] = useState<Runtime>('ONE_YEAR');
  const [notes, setNotes] = useState('');

  // Process form state
  const [processStatus, setProcessStatus] = useState<RequestStatus>('APPROVED');
  const [processNotes, setProcessNotes] = useState('');
  const [newPartnerId, setNewPartnerId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['requests', { page, status: statusFilter, scope: canProcess ? 'all' : 'mine' }],
    queryFn: () =>
      canProcess
        ? listRequests({ page, status: statusFilter || undefined })
        : getMyRequests({ page, status: statusFilter || undefined }),
  });

  // Full request (with comment thread) for the expanded row
  const { data: expanded } = useQuery({
    queryKey: ['request', expandedId],
    queryFn: () => getRequest(expandedId!),
    enabled: !!expandedId,
  });

  const { data: customers } = useQuery({
    queryKey: ['customers', 'dropdown'],
    queryFn: () => listCustomers({ limit: 100 }),
    enabled: isCreateOpen && isPartner,
  });

  const { data: products } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: getActiveProducts,
    enabled: isCreateOpen,
  });

  // Partners to choose from when an owner/admin approves a partner-change request.
  const { data: reassignPartners } = useQuery({
    queryKey: ['partners', 'reassign'],
    queryFn: () => listPartners({ limit: 100 }),
    enabled: processModalRequest?.type === 'CHANGE_PARTNER',
  });

  const invalidate = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: ['requests'] });
    if (id) queryClient.invalidateQueries({ queryKey: ['request', id] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createRequest({
        customerId: isPartner ? customerId || undefined : undefined,
        type: requestType,
        details: { productId: productId || undefined, deviceCount, runtime, notes },
      }),
    onSuccess: () => {
      toast.success('Antrag gesendet');
      invalidate();
      setIsCreateOpen(false);
      resetCreateForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const commentMutation = useMutation({
    mutationFn: (id: string) =>
      addRequestComment(id, commentText.trim(), isCustomer ? undefined : !commentVisibleToCustomer),
    onSuccess: (_d, id) => {
      setCommentText('');
      setCommentVisibleToCustomer(false);
      invalidate(id);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const forwardMutation = useMutation({
    mutationFn: (id: string) => forwardRequest(id, forwardText.trim() || undefined),
    onSuccess: (_d, id) => {
      toast.success('An Admin weitergeleitet');
      setForwardText('');
      invalidate(id);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const processMutation = useMutation({
    mutationFn: () =>
      processRequest(processModalRequest!.id, {
        status: processStatus,
        response: {
          notes: processNotes,
          ...(processModalRequest!.type === 'CHANGE_PARTNER' && processStatus === 'APPROVED'
            ? { newPartnerId }
            : {}),
        },
      }),
    onSuccess: () => {
      toast.success('Antrag bearbeitet');
      invalidate(processModalRequest!.id);
      setProcessModalRequest(null);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const resetCreateForm = () => {
    setCustomerId('');
    setRequestType('EXTEND_LICENSE');
    setProductId('');
    setDeviceCount(1);
    setRuntime('ONE_YEAR');
    setNotes('');
  };

  const toggleExpand = (id: string) => {
    setExpandedId((cur) => (cur === id ? null : id));
    setCommentText('');
    setCommentVisibleToCustomer(false);
    setForwardText('');
  };

  const renderThread = (comments: RequestComment[] | undefined) => {
    if (!comments || comments.length === 0) {
      return <p className="text-sm text-gray-500">Noch keine Einträge.</p>;
    }
    return (
      <div className="space-y-4">
        {comments.map((c) => (
          <div key={c.id} className="flex gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="p-1.5 bg-gray-100 rounded-full">{commentIcon(c.action)}</div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                {commentActionLabel[c.action] || c.action}
                {c.visibility === 'INTERNAL' && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                    <HiLockClosed className="w-3 h-3" /> intern
                  </span>
                )}
              </p>
              {c.message && <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-wrap">{c.message}</p>}
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-gray-400">{formatDateTime(c.createdAt)}</p>
                {c.performedByName && (
                  <p className="text-xs text-gray-400">
                    von {c.performedByName}{c.performedByRole ? ` (${c.performedByRole})` : ''}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderExpanded = (r: PartnerRequest) => {
    const detail = expanded && expanded.id === r.id ? expanded : undefined;
    const d = parseJSON<{ deviceCount?: number; runtime?: string; licenseId?: string }>(r.details, {});
    const canComment = true;
    const canForward = isPartner && r.stage === 'PARTNER' && r.status === 'PENDING';
    const canProcessThis = canProcess && r.status === 'PENDING';

    return (
      <div className="px-4 pb-4 pt-2 bg-gray-50 border-t border-gray-200">
        {/* Meta */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 mb-4">
          {d.runtime && <span>Laufzeit: <span className="font-medium">{getRuntimeLabel(d.runtime)}</span></span>}
          {typeof d.deviceCount === 'number' && <span>Geräte: <span className="font-medium">{d.deviceCount}</span></span>}
          {r.partner && <span>Partner: <span className="font-medium">{r.partner.name}</span></span>}
          {r.customer && <span>Kunde: <span className="font-medium">{r.customer.name}</span></span>}
        </div>

        {/* Thread */}
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Verlauf</h4>
        {renderThread(detail?.comments)}

        {/* Comment box */}
        {canComment && (
          <div className="mt-4">
            <div className="flex items-start gap-2">
              <textarea
                value={expandedId === r.id ? commentText : ''}
                onChange={(e) => setCommentText(e.target.value)}
                rows={2}
                placeholder={isCustomer ? 'Kommentar hinzufügen…' : 'Interne Notiz oder Nachricht…'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
              <Button
                size="sm"
                isLoading={commentMutation.isPending}
                disabled={!commentText.trim()}
                onClick={() => commentMutation.mutate(r.id)}
              >
                <HiPaperAirplane className="w-4 h-4" />
              </Button>
            </div>
            {!isCustomer && (
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 select-none">
                <input
                  type="checkbox"
                  checked={commentVisibleToCustomer}
                  onChange={(e) => setCommentVisibleToCustomer(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Für Kunde sichtbar
                <span className="text-gray-400">
                  {commentVisibleToCustomer ? '(der Kunde sieht diesen Kommentar)' : '(intern – nur Partner & Admin)'}
                </span>
              </label>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex flex-wrap items-end gap-3">
          {canForward && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Kommentar für Admin (optional)</label>
                <input
                  type="text"
                  value={forwardText}
                  onChange={(e) => setForwardText(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm w-64"
                />
              </div>
              <Button isLoading={forwardMutation.isPending} onClick={() => forwardMutation.mutate(r.id)}>
                <HiArrowRight className="w-4 h-4 mr-2" />
                An Admin weiterleiten
              </Button>
            </div>
          )}
          {canProcessThis && (
            <Button
              onClick={() => {
                setProcessStatus('APPROVED');
                setProcessNotes('');
                setNewPartnerId('');
                setProcessModalRequest(r);
              }}
            >
              <HiCheck className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <CardHeader
        title="Requests"
        description={canProcess ? 'Anträge verwalten' : 'Deine Anträge'}
        action={
          canCreate && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <HiPlus className="w-4 h-4 mr-2" />
              Neuer Antrag
            </Button>
          )
        }
      />

      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <Select
            options={[{ value: '', label: 'Alle Status' }, ...REQUEST_STATUS_OPTIONS]}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RequestStatus | '')}
            className="w-48"
          />
        </div>

        {/* Expandable list */}
        <div className="divide-y divide-gray-200">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Lädt…</div>
          ) : (data?.data.length ?? 0) === 0 ? (
            <div className="p-8 text-center text-gray-500">Keine Anträge gefunden</div>
          ) : (
            data!.data.map((r) => {
              const isOpen = expandedId === r.id;
              return (
                <div key={r.id}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(r.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    {isOpen ? (
                      <HiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <HiChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-gray-900">{typeLabel(r.type)}</span>
                      <span className="text-gray-500 text-sm ml-2 truncate">
                        {r.customer?.name || r.partner?.name || '-'}
                      </span>
                    </span>
                    <span className={`hidden sm:inline-block text-xs px-2 py-0.5 rounded-full ${stageClasses[r.stage]}`}>
                      {stageLabel[r.stage]}
                    </span>
                    <Badge status={r.status}>{r.status}</Badge>
                    <span className="hidden md:block text-sm text-gray-400 w-24 text-right">
                      {formatDate(r.createdAt)}
                    </span>
                  </button>
                  {isOpen && renderExpanded(r)}
                </div>
              );
            })
          )}
        </div>

        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Request Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Neuer Antrag" size="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          <Select
            label="Antragstyp"
            options={REQUEST_TYPE_OPTIONS.filter((o) => o.value !== 'CHANGE_PARTNER' || isCustomer)}
            value={requestType}
            onChange={(e) => setRequestType(e.target.value as RequestType)}
            required
          />
          {isPartner && (
            <Select
              label="Kunde (optional)"
              options={[
                { value: '', label: 'Kunde wählen…' },
                ...(customers?.data.map((c) => ({ value: c.id, label: c.name })) || []),
              ]}
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            />
          )}
          {requestType === 'CHANGE_PARTNER' ? (
            <p className="text-sm bg-purple-50 text-purple-700 rounded-lg px-3 py-2">
              Dein Antrag geht direkt an den Owner. Dein aktueller Partner wird darüber nicht informiert.
            </p>
          ) : (
            <>
              <Select
                label="Produkt"
                options={products?.map((p) => ({ value: p.id, label: p.name })) || []}
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder="Produkt wählen"
              />
              <Input
                type="number"
                label="Geräteanzahl"
                value={deviceCount}
                onChange={(e) => setDeviceCount(parseInt(e.target.value))}
                min={1}
              />
              <Select
                label="Laufzeit"
                options={[...RUNTIME_OPTIONS]}
                value={runtime}
                onChange={(e) => setRuntime(e.target.value as Runtime)}
              />
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {requestType === 'CHANGE_PARTNER' ? 'Grund für den Partnerwechsel' : 'Anmerkung'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={
                requestType === 'CHANGE_PARTNER'
                  ? 'Warum möchtest du den Partner wechseln?'
                  : 'Worum geht es? (z. B. Verlängerung um 1 Jahr)'
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Antrag senden
            </Button>
          </div>
        </form>
      </Modal>

      {/* Process Request Modal */}
      <Modal isOpen={!!processModalRequest} onClose={() => setProcessModalRequest(null)} title="Antrag bearbeiten">
        {processModalRequest && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              processMutation.mutate();
            }}
            className="space-y-4"
          >
            <Select
              label="Entscheidung"
              options={[
                { value: 'APPROVED', label: 'Genehmigen' },
                { value: 'REJECTED', label: 'Ablehnen' },
                { value: 'COMPLETED', label: 'Abschließen' },
              ]}
              value={processStatus}
              onChange={(e) => setProcessStatus(e.target.value as RequestStatus)}
              required
            />
            {processModalRequest.type === 'CHANGE_PARTNER' && processStatus === 'APPROVED' && (
              <Select
                label="Neuer Partner"
                options={[
                  { value: '', label: 'Partner wählen…' },
                  ...(reassignPartners?.data
                    .filter((p) => p.id !== parseJSON<{ currentPartnerId?: string }>(processModalRequest.details, {}).currentPartnerId)
                    .map((p) => ({ value: p.id, label: p.name })) || []),
                ]}
                value={newPartnerId}
                onChange={(e) => setNewPartnerId(e.target.value)}
                required
              />
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anmerkung</label>
              <textarea
                value={processNotes}
                onChange={(e) => setProcessNotes(e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => setProcessModalRequest(null)}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                isLoading={processMutation.isPending}
                disabled={processModalRequest.type === 'CHANGE_PARTNER' && processStatus === 'APPROVED' && !newPartnerId}
              >
                Speichern
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
